import { useEffect, useRef, useState } from "react";
import { Paperclip, ArrowUp, CircleNotch, UploadSimple, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errDetail } from "../lib/api";
import { ModelPicker } from "./ModelPicker";
import { AttachmentCard } from "./AttachmentCard";

export const ChatInput = ({ onSend, onStop, busy, providers, selection, onSelectionChange }) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);
  const dragDepth = useRef(0);
  const imageMode = selection.provider === "image";

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        setAttachments((prev) => [...prev, data]);
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} attached`);
    } catch (err) {
      toast.error(errDetail(err));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const onDragEnter = (e) => { e.preventDefault(); if (hasFiles(e)) { dragDepth.current += 1; setDragging(true); } };
    const onDragOver = (e) => { if (hasFiles(e)) e.preventDefault(); };
    const onDragLeave = (e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } };
    const onDrop = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length) uploadFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const files = items.filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter(Boolean);
    if (files.length) {
      e.preventDefault();
      uploadFiles(files.map((f, i) =>
        new File([f], (f.name && f.name !== "image.png") ? f.name : `pasted-image-${Date.now()}-${i}.png`,
          { type: f.type || "image/png" })));
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    onSend(trimmed, attachments);
    setText("");
    setAttachments([]);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  return (
    <div className="px-6 pb-6 pt-2 bg-[var(--bg)]">
      {dragging && (
        <div className="fixed inset-0 z-50 bg-[var(--bg)]/85 backdrop-blur-sm flex flex-col items-center justify-center gap-3 pointer-events-none" data-testid="drop-overlay">
          <span className="w-16 h-16 rounded-full flex items-center justify-center border-2 border-dashed" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
            <UploadSimple size={28} weight="bold" />
          </span>
          <p className="text-lg font-serif-display font-semibold text-[var(--text)]">Drop files anywhere to attach</p>
        </div>
      )}
      <div className="max-w-3xl mx-auto">
        <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-3xl px-4 pt-3 pb-2.5 focus-within:border-[var(--accent)]/60 transition-colors duration-300"
          style={{ boxShadow: "var(--shadow)" }} data-testid="chat-input-dropzone">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2.5" data-testid="pending-attachments">
              {attachments.map((a) => (
                <AttachmentCard key={a.id} att={a} compact onRemove={(id) => setAttachments((p) => p.filter((x) => x.id !== id))} />
              ))}
            </div>
          )}
          <textarea ref={taRef} data-testid="chat-input" rows={1} value={text}
            placeholder={imageMode ? "Describe the image — attach a reference image to edit it…" : "How can we help you today?"}
            onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px"; }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            onPaste={onPaste}
            className="w-full bg-transparent resize-none outline-none text-[15px] text-[var(--text)] placeholder:text-[var(--muted)] py-1 max-h-[200px]" />
          <div className="flex items-center justify-between mt-1.5">
            <div className="flex items-center gap-1">
              <button data-testid="attach-file-button" onClick={() => fileRef.current?.click()} disabled={uploading} title="Attach file">
                <span className="flex text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-2 rounded-lg">
                  {uploading ? <CircleNotch size={17} className="animate-spin" /> : <Paperclip size={17} />}
                </span>
              </button>
              <input ref={fileRef} type="file" multiple hidden onChange={onFileInput}
                accept={imageMode ? "image/*" : "image/*,.pdf,.txt,.csv,.md,.json,.docx"} data-testid="file-input" />
              <ModelPicker providers={providers} selection={selection} onChange={onSelectionChange} />
            </div>
            {busy ? (
              <button data-testid="stop-response-button" onClick={onStop} title="Stop response"
                className="rounded-full text-white p-2.5 transition-colors duration-300"
                style={{ background: "var(--text)" , color: "var(--bg)" }}>
                <Stop size={16} weight="fill" />
              </button>
            ) : (
              <button data-testid="chat-submit-button" onClick={submit} disabled={!text.trim()}
                className="rounded-full text-white p-2.5 transition-colors duration-300 disabled:opacity-30"
                style={{ background: "var(--accent)" }}>
                <ArrowUp size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-[var(--muted)] text-center mt-2.5">
          {imageMode ? "Attach an image to use it as a reference for editing." : "Switch models anytime — even mid-conversation. Drag & drop files anywhere to attach."}
        </p>
      </div>
    </div>
  );
};
